"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.STOCK_ADJUSTMENT_SERIES = exports.STOCK_OPENING_SERIES = void 0;
exports.emptyPosition = emptyPosition;
exports.averageCost = averageCost;
exports.receiveStock = receiveStock;
exports.costIssue = costIssue;
exports.replayStock = replayStock;
exports.planStockIssue = planStockIssue;
exports.planStockReceipt = planStockReceipt;
exports.planStockReturn = planStockReturn;
exports.planStockCount = planStockCount;
exports.lowestRunningQuantity = lowestRunningQuantity;
exports.costOfSalesLines = costOfSalesLines;
exports.stockDocumentIntent = stockDocumentIntent;
const money_1 = require("../money");
const document_rules_1 = require("../posting/document-rules");
/**
 * المخزون — محرك خالص: تكلفة الصرف (متوسط مرجح أو FIFO)، وفحص الرصيد السالب،
 * وفروق الجرد، وسطور القيود. لا قاعدة بيانات ولا وقت: المتصل يمرر حركات الصنف المخزنة
 * مرتبة (بالتاريخ ثم بترتيب التسجيل)، والمحرك يعيد بناء الموقف منها.
 *
 * `BLOCKED-ACCOUNTING`: طريقة التكلفة (المتوسط المرجح افتراضى موصى به، وFIFO بديل)،
 * وتسعير المرتجع بتكلفة البيع الأصلية، ومعالجة الرصيد السالب، وحساب فروق الجرد —
 * كلها خيارات فى إعدادات الشركة حتى يعتمدها محاسب.
 */
/** سلاسل مستندات المخزون — الصيغة `BLOCKED-ACCOUNTING` كبقية السلاسل. */
exports.STOCK_OPENING_SERIES = 'SO';
exports.STOCK_ADJUSTMENT_SERIES = 'SA';
const round = (value) => value.round(money_1.PRESENTATION_SCALE, 'half_up');
function emptyPosition() {
    return {
        quantity: money_1.Money.zero(),
        value: money_1.Money.zero(),
        layers: [],
        lastUnitCost: null,
        lastDate: null,
    };
}
/** تكلفة الوحدة الحالية للعرض والتقييم: القيمة ÷ الكمية، وصفر حين لا رصيد موجب. */
function averageCost(position) {
    if (!position.quantity.isPositive())
        return money_1.Money.zero();
    return position.value.div(position.quantity);
}
/**
 * الوارد: يضيف للكمية والقيمة، وطبقة FIFO جديدة. وارد على رصيد سالب (عجز مسموح) يسد
 * العجز أولاً، فلا تبقى طبقة لكمية صُرفت بالفعل.
 */
function receiveStock(position, date, quantity, value) {
    const qty = money_1.Money.of(quantity);
    const val = money_1.Money.of(value);
    const layers = position.layers.map((layer) => ({ ...layer }));
    const shortage = position.quantity.isNegative() ? position.quantity.negate() : money_1.Money.zero();
    const fill = shortage.gt(qty) ? qty : shortage;
    const layerQty = qty.sub(fill);
    if (layerQty.isPositive()) {
        const layerValue = fill.isZero() ? val : round(val.mul(layerQty).div(qty));
        layers.push({ quantity: layerQty, value: layerValue });
    }
    return {
        quantity: position.quantity.add(qty),
        value: position.value.add(val),
        layers,
        lastUnitCost: qty.isPositive() ? val.div(qty) : position.lastUnitCost,
        lastDate: maxDate(position.lastDate, date),
    };
}
/**
 * تكلفة صرف كمية من الصنف بالطريقة المختارة.
 *
 * - المتوسط المرجح: الكمية × (القيمة ÷ الرصيد) مقربة لقرشين.
 * - FIFO: من أقدم طبقة فما بعدها؛ الطبقة المستنفدة تخرج بقيمتها كاملة.
 * - صرف كل الرصيد يأخذ القيمة كلها فى الطريقتين، فلا يبقى فرق تقريب على صنف رصيده صفر.
 *
 * لا يفحص السماح بالسالب — ذلك عمل `planStockIssue` الذى يجمع المشاكل.
 */
function costIssue(position, date, quantity, method) {
    const qty = money_1.Money.of(quantity);
    const available = position.quantity.isPositive() ? position.quantity : money_1.Money.zero();
    const fromStock = qty.gt(available) ? available : qty;
    const shortfall = qty.sub(fromStock);
    let layers = position.layers.map((layer) => ({ ...layer }));
    let stockCost = money_1.Money.zero();
    if (fromStock.isPositive()) {
        if (fromStock.eq(available)) {
            // الرصيد كله: القيمة كلها، وتفرغ الطبقات
            stockCost = position.value;
            layers = [];
        }
        else if (method === 'weighted_average') {
            stockCost = round(position.value.mul(fromStock).div(available));
            layers = consumeLayers(layers, fromStock).layers;
        }
        else {
            const consumed = consumeLayers(layers, fromStock);
            stockCost = consumed.cost;
            layers = consumed.layers;
        }
    }
    const shortfallCost = shortfall.isPositive()
        ? round(shortfall.mul(position.lastUnitCost ?? money_1.Money.zero()))
        : money_1.Money.zero();
    const cost = stockCost.add(shortfallCost);
    return {
        cost,
        shortfall,
        position: {
            quantity: position.quantity.sub(qty),
            value: position.value.sub(cost),
            layers,
            lastUnitCost: position.lastUnitCost,
            lastDate: maxDate(position.lastDate, date),
        },
    };
}
function consumeLayers(layers, quantity) {
    let remaining = quantity;
    let cost = money_1.Money.zero();
    const left = [];
    for (const layer of layers) {
        if (!remaining.isPositive()) {
            left.push(layer);
            continue;
        }
        if (!layer.quantity.gt(remaining)) {
            cost = cost.add(layer.value);
            remaining = remaining.sub(layer.quantity);
            continue;
        }
        const part = round(layer.value.mul(remaining).div(layer.quantity));
        cost = cost.add(part);
        left.push({ quantity: layer.quantity.sub(remaining), value: layer.value.sub(part) });
        remaining = money_1.Money.zero();
    }
    return { cost, layers: left };
}
/**
 * يعيد بناء موقف الصنف من حركاته المخزنة. القيمة مجموع القيم المرحّلة كما هى (لا
 * إعادة تسعير)، والطبقات تُستهلك بنفس ترتيب الحركات — فما يُحسب الآن هو ما حُسب وقتها.
 */
function replayStock(movements) {
    let position = emptyPosition();
    for (const movement of movements) {
        const quantity = money_1.Money.of(movement.quantity);
        const value = money_1.Money.of(movement.value);
        if (quantity.isPositive()) {
            position = receiveStock(position, movement.date, quantity, value);
        }
        else if (quantity.isNegative()) {
            const issued = costIssue(position, movement.date, quantity.negate(), 'fifo');
            // القيمة المخزنة هى الحقيقة؛ الطبقات وحدها من إعادة البناء
            position = {
                ...issued.position,
                value: position.value.add(value),
            };
        }
    }
    return position;
}
/**
 * صرف أصناف مخزنية لمستند (فاتورة بيع): تكلفة كل سطر من موقف صنفه بعد السطور السابقة
 * فى نفس المستند. المشاكل كلها معًا: كمية غير موجبة، صنف بلا موقف، تاريخ قبل آخر حركة،
 * ورصيد لا يكفى حين لا يسمح الإعداد بالسالب.
 */
function planStockIssue(input) {
    const issues = [];
    const positions = new Map(input.positions);
    const lines = [];
    for (const line of input.lines) {
        const quantity = money_1.Money.of(line.quantity);
        const position = positions.get(line.itemId);
        if (!checkLineBasics(issues, line.lineIndex, quantity, position, input.date))
            continue;
        const current = position;
        if (!input.allowNegativeStock && quantity.gt(current.quantity)) {
            issues.push({
                code: 'STOCK_INSUFFICIENT',
                message: 'not enough stock to issue this quantity',
                lineIndex: line.lineIndex,
                details: {
                    itemId: line.itemId,
                    available: current.quantity.toString(),
                    requested: quantity.toString(),
                },
            });
            continue;
        }
        const issued = costIssue(current, input.date, quantity, input.method);
        positions.set(line.itemId, issued.position);
        lines.push({
            lineIndex: line.lineIndex,
            itemId: line.itemId,
            quantity: quantity.negate(),
            value: issued.cost.negate(),
        });
    }
    if (issues.length > 0)
        return { ok: false, issues };
    return { ok: true, value: { lines, positions } };
}
/** وارد لمستند (فاتورة مورد، رصيد افتتاحى): المشاكل كلها معًا، والقيمة لا تكون سالبة. */
function planStockReceipt(input) {
    const issues = [];
    const positions = new Map(input.positions);
    const lines = [];
    for (const line of input.lines) {
        const quantity = money_1.Money.of(line.quantity);
        const value = money_1.Money.of(line.value);
        const position = positions.get(line.itemId);
        if (!checkLineBasics(issues, line.lineIndex, quantity, position, input.date))
            continue;
        if (value.isNegative()) {
            issues.push({
                code: 'STOCK_VALUE_NEGATIVE',
                message: 'received stock cannot have a negative value',
                lineIndex: line.lineIndex,
            });
            continue;
        }
        positions.set(line.itemId, receiveStock(position, input.date, quantity, value));
        lines.push({ lineIndex: line.lineIndex, itemId: line.itemId, quantity, value });
    }
    if (issues.length > 0)
        return { ok: false, issues };
    return { ok: true, value: { lines, positions } };
}
/**
 * مرتجع مبيعات إلى المخزون بتكلفة البيع الأصلية (لا بالمتوسط الحالى): تكلفة الوحدة =
 * قيمة ما صُرف على الفاتورة ÷ كميته، ورد كل المتبقى يأخذ قيمته كلها. لا يُرد أكثر مما
 * صُرف على تلك الفاتورة ولم يُرد بعد.
 */
function planStockReturn(input) {
    const issues = [];
    const positions = new Map(input.positions);
    const remaining = new Map([...input.sold].map(([itemId, sold]) => [
        itemId,
        { quantity: money_1.Money.of(sold.quantity), value: money_1.Money.of(sold.value) },
    ]));
    const lines = [];
    for (const line of input.lines) {
        const quantity = money_1.Money.of(line.quantity);
        const position = positions.get(line.itemId);
        if (!checkLineBasics(issues, line.lineIndex, quantity, position, input.date))
            continue;
        const sold = remaining.get(line.itemId);
        if (!sold || quantity.gt(sold.quantity)) {
            issues.push({
                code: 'STOCK_RETURN_EXCEEDS_SOLD',
                message: 'cannot return more than was issued on the original invoice',
                lineIndex: line.lineIndex,
                details: {
                    itemId: line.itemId,
                    returnable: (sold?.quantity ?? money_1.Money.zero()).toString(),
                    requested: quantity.toString(),
                },
            });
            continue;
        }
        const value = quantity.eq(sold.quantity)
            ? sold.value
            : round(sold.value.mul(quantity).div(sold.quantity));
        remaining.set(line.itemId, {
            quantity: sold.quantity.sub(quantity),
            value: sold.value.sub(value),
        });
        positions.set(line.itemId, receiveStock(position, input.date, quantity, value));
        lines.push({ lineIndex: line.lineIndex, itemId: line.itemId, quantity, value });
    }
    if (issues.length > 0)
        return { ok: false, issues };
    return { ok: true, value: { lines, positions } };
}
/**
 * الجرد: الفرق بين المعدود والدفترى حركةُ تسوية. العجز يُصرف بتكلفة الطريقة المختارة،
 * والزيادة تدخل بتكلفة الوحدة المرسلة أو المتوسط الحالى أو آخر تكلفة واردة. صنف لا فرق
 * له لا يُنتج حركة، ومستند بلا أى فرق يُرفض — لا قيد صفرى.
 */
function planStockCount(input) {
    const issues = [];
    const positions = new Map(input.positions);
    const lines = [];
    const seen = new Set();
    for (const line of input.lines) {
        const counted = money_1.Money.of(line.countedQuantity);
        const position = positions.get(line.itemId);
        if (seen.has(line.itemId)) {
            issues.push({
                code: 'STOCK_DUPLICATE_ITEM',
                message: 'an item appears more than once in the same count',
                lineIndex: line.lineIndex,
            });
            continue;
        }
        seen.add(line.itemId);
        if (counted.isNegative()) {
            issues.push({
                code: 'STOCK_QUANTITY',
                message: 'counted quantity cannot be negative',
                lineIndex: line.lineIndex,
            });
            continue;
        }
        if (!position) {
            issues.push(unknownItem(line.lineIndex));
            continue;
        }
        if (isBackdated(position, input.date)) {
            issues.push(backdated(line.lineIndex, position));
            continue;
        }
        const difference = counted.sub(position.quantity);
        if (difference.isZero())
            continue;
        if (difference.isPositive()) {
            const unitCost = line.unitCost !== undefined && line.unitCost !== null
                ? money_1.Money.of(line.unitCost)
                : position.quantity.isPositive()
                    ? averageCost(position)
                    : position.lastUnitCost;
            if (!unitCost || unitCost.isNegative()) {
                issues.push({
                    code: 'STOCK_COST_UNKNOWN',
                    message: 'no cost is known for this item; send unitCost for the surplus',
                    lineIndex: line.lineIndex,
                });
                continue;
            }
            const value = round(difference.mul(unitCost));
            positions.set(line.itemId, receiveStock(position, input.date, difference, value));
            lines.push({
                lineIndex: line.lineIndex,
                itemId: line.itemId,
                quantity: difference,
                value,
                countedQuantity: counted,
                bookQuantity: position.quantity,
            });
        }
        else {
            const issued = costIssue(position, input.date, difference.negate(), input.method);
            positions.set(line.itemId, issued.position);
            lines.push({
                lineIndex: line.lineIndex,
                itemId: line.itemId,
                quantity: difference,
                value: issued.cost.negate(),
                countedQuantity: counted,
                bookQuantity: position.quantity,
            });
        }
    }
    if (issues.length === 0 && lines.length === 0) {
        issues.push({
            code: 'STOCK_COUNT_NO_DIFFERENCE',
            message: 'the count matches the books for every item; nothing to post',
        });
    }
    if (issues.length > 0)
        return { ok: false, issues };
    return { ok: true, value: { lines, positions } };
}
/**
 * فحص رصيد سالب على سلسلة حركات كاملة (بعد إضافة الجديد): أدنى رصيد جارٍ. يستعمله من
 * يريد التحقق من تاريخ صنف كامل — كما يفعل trigger القاعدة.
 */
function lowestRunningQuantity(movements) {
    let running = money_1.Money.zero();
    let lowest = money_1.Money.zero();
    for (const movement of movements) {
        running = running.add(movement.quantity);
        if (running.lt(lowest))
            lowest = running;
    }
    return lowest;
}
/**
 * سطور قيد حركات البيع والمرتجع داخل قيد المستند نفسه: الصرف مدين تكلفة المبيعات ودائن
 * المخزون، والمرتجع عكسه. مجمّعة بالحساب، والقيمة الصفرية لا تُنتج سطرًا.
 */
function costOfSalesLines(currency, movements) {
    return offsetLines(currency, movements.map((movement) => ({
        inventory: movement.accounts.inventory,
        offset: movement.accounts.cogs,
        value: money_1.Money.of(movement.value),
    })));
}
function offsetLines(currency, movements) {
    const inventory = new Map();
    const offset = new Map();
    for (const movement of movements) {
        inventory.set(movement.inventory, (inventory.get(movement.inventory) ?? money_1.Money.zero()).add(movement.value));
        offset.set(movement.offset, (offset.get(movement.offset) ?? money_1.Money.zero()).add(movement.value));
    }
    const lines = [];
    // الطرف المقابل أولاً (تكلفة/فروق)، ثم المخزون — كترتيب قيد الصرف المعتاد
    for (const [account, amount] of offset) {
        if (amount.isNegative())
            lines.push((0, document_rules_1.debitLine)(account, amount.negate(), currency));
        if (amount.isPositive())
            lines.push((0, document_rules_1.creditLine)(account, amount, currency));
    }
    for (const [account, amount] of inventory) {
        if (amount.isPositive())
            lines.push((0, document_rules_1.debitLine)(account, amount, currency));
        if (amount.isNegative())
            lines.push((0, document_rules_1.creditLine)(account, amount.negate(), currency));
    }
    return lines;
}
/**
 * قيد مستند مخزون مستقل: الافتتاحى مدين المخزون دائن حساب الافتتاح، والتسوية مدين
 * المخزون دائن فروق الجرد للزيادة وعكسه للعجز. مجمّع بالحساب.
 */
function stockDocumentIntent(ctx, input) {
    const lines = offsetLines(ctx.currency, input.lines.map((line) => ({
        inventory: line.inventoryAccount,
        offset: input.offsetAccount,
        value: money_1.Money.of(line.value),
    })));
    return {
        companyId: ctx.companyId,
        sourceType: input.kind === 'opening' ? 'stock_opening' : 'stock_adjustment',
        sourceId: input.sourceId,
        postingDate: input.postingDate,
        idempotencyKey: input.idempotencyKey,
        reference: input.reference ?? null,
        memo: input.memo ?? null,
        createdBy: ctx.createdBy ?? null,
        lines,
    };
}
function checkLineBasics(issues, lineIndex, quantity, position, date) {
    if (!quantity.isPositive()) {
        issues.push({ code: 'STOCK_QUANTITY', message: 'quantity must be positive', lineIndex });
        return false;
    }
    if (!position) {
        issues.push(unknownItem(lineIndex));
        return false;
    }
    if (isBackdated(position, date)) {
        issues.push(backdated(lineIndex, position));
        return false;
    }
    return true;
}
function isBackdated(position, date) {
    return position.lastDate !== null && date < position.lastDate;
}
function unknownItem(lineIndex) {
    return {
        code: 'STOCK_ITEM_UNKNOWN',
        message: 'item is not a stock item of this company',
        lineIndex,
    };
}
/**
 * حركة قبل آخر حركة للصنف تُرفض: التكلفة محسوبة على ما سبقها، والحركات اللاحقة
 * مرحّلة بتكلفتها ولا تُعاد. التاريخ نفسه مسموح.
 */
function backdated(lineIndex, position) {
    return {
        code: 'STOCK_BACKDATED',
        message: 'stock movement date is before the item last movement ' + position.lastDate,
        lineIndex,
        details: { lastMovementDate: position.lastDate ?? '' },
    };
}
function maxDate(current, date) {
    return current === null || date > current ? date : current;
}
//# sourceMappingURL=inventory.js.map